// Parts4Drive Marketplace & Inventory - Unified Direct Auth Client
// Edge-compatible, cookie & token multi-tenant session manager
'use client'

import { useState, useEffect } from 'react'

const API_BASE = 'https://parts4drive.com'

export const SUPERADMIN_EMAILS = Object.freeze([
  'admin@parts4drive.com',
  'director@parts4drive.com',
  'ngisense.inc@gmail.com',
])

export function isSuperAdmin(user: any): boolean {
  if (!user) return false
  const email = (user.email || '').toLowerCase().trim()
  return (
    SUPERADMIN_EMAILS.some((e) => e.toLowerCase() === email) ||
    user.role === 'SUPERADMIN' ||
    user.role === 'ADMIN'
  )
}

export interface AuthResponse {
  success: boolean
  requiresDeviceOtp?: boolean
  requiresOtp?: boolean
  requiresVerification?: boolean
  email?: string
  deviceId?: string
  deviceName?: string
  user?: any
  token?: string
  data?: any
  error?: any
  message?: string
  status?: number
}

// Auth state
let currentUser: any = null
let authListeners: Array<(user: any) => void> = []
let authInitialized = false

function notifyListeners() {
  authListeners.forEach((listener) => {
    try {
      if (!currentUser) {
        listener(null)
      } else {
        const payload = typeof currentUser === 'object' ? { ...currentUser, user: currentUser } : currentUser
        listener(payload)
      }
    } catch (e) {
      console.warn('Auth listener error:', e)
    }
  })
}

export const authClient = {
  storeToken(_token?: string) {
    // Better Auth manages sessions exclusively via secure HttpOnly cookies.
    // Purge any legacy storage tokens to enforce clean client state.
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('p4d_marketplace_token')
      localStorage.removeItem('p4d_auth_token')
      localStorage.removeItem('p4d_token')
    }
  },

  clearToken() {
    if (typeof localStorage !== 'undefined') {
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (
          key &&
          (key.startsWith('p4d_') ||
            key.startsWith('better-auth.') ||
            key.includes('session') ||
            key.includes('token'))
        ) {
          keysToRemove.push(key)
        }
      }
      for (const k of keysToRemove) {
        localStorage.removeItem(k)
      }
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear()
    }
    if (typeof document !== 'undefined') {
      const cookies = [
        'better-auth.session_token',
        'p4d_session_token',
        'p4d_user_email',
        'p4d_auth_token',
        'p4d_marketplace_token',
      ]
      const domains = [
        window.location.hostname,
        `.${window.location.hostname}`,
        '.parts4drive.com',
        '',
      ]
      for (const cName of cookies) {
        for (const d of domains) {
          document.cookie = `${cName}=; Max-Age=0; path=/; ${d ? `domain=${d};` : ''} expires=Thu, 01 Jan 1970 00:00:00 GMT`
        }
      }
    }
  },

  getToken(): string {
    return ''
  },

  async signInEmail(
    emailOrObj: string | { email: string; password: string; deviceId?: string; deviceName?: string },
    passwordParam?: string
  ): Promise<AuthResponse> {
    const email = typeof emailOrObj === 'object' ? emailOrObj.email : emailOrObj
    const password = typeof emailOrObj === 'object' ? emailOrObj.password : (passwordParam || '')

    const apiBase =
      typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com')
        ? ''
        : API_BASE

    let activeDeviceId =
      typeof emailOrObj === 'object' && emailOrObj.deviceId ? emailOrObj.deviceId : ''
    if (!activeDeviceId && typeof localStorage !== 'undefined') {
      activeDeviceId = localStorage.getItem('p4d_device_id') || ''
      if (!activeDeviceId) {
        activeDeviceId = `dev_${crypto.randomUUID().slice(0, 12)}`
        localStorage.setItem('p4d_device_id', activeDeviceId)
      }
    }

    const activeDeviceName =
      typeof emailOrObj === 'object' && emailOrObj.deviceName
        ? emailOrObj.deviceName
        : typeof navigator !== 'undefined'
          ? navigator.userAgent
          : 'P4D Web Client'

    try {
      const res = await fetch(`${apiBase}/api/v1/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          deviceId: activeDeviceId,
          deviceName: activeDeviceName,
          source: 'unified',
          app: 'parts4drive',
        }),
      })

      let data: any = {}
      try {
        data = await res.json()
      } catch {
        data = {}
      }

      if (data.requiresDeviceOtp || data.requiresOtp || data.requiresVerification) {
        return {
          success: false,
          requiresDeviceOtp: !!(data.requiresDeviceOtp),
          requiresOtp: !!(data.requiresOtp || data.requiresDeviceOtp),
          requiresVerification: !!(data.requiresVerification || data.data?.requiresVerification),
          email: data.email || email,
          deviceId: activeDeviceId,
          deviceName: activeDeviceName,
          message: data.message || 'Verification code required.',
          data,
          error: null,
        }
      }

      if (data.success || res.ok) {
        const token = data.token || data.data?.token || data.session?.token
        if (token) {
          this.storeToken(token)
        }
        currentUser = data.data?.user || data.data || data.user
        authInitialized = true
        notifyListeners()
        return {
          success: true,
          user: currentUser,
          token,
          message: data.message || 'Authentication successful.',
          data: { user: currentUser, token, ...data },
          error: null,
        }
      }

      const errMessage =
        data.error || data.message || 'Invalid email or password.'
      return {
        success: false,
        error: typeof errMessage === 'string' ? errMessage : JSON.stringify(errMessage),
        message: typeof errMessage === 'string' ? errMessage : JSON.stringify(errMessage),
        data: null,
      }
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || 'Authentication failed. Please check network connection.',
        message: err?.message || 'Authentication failed. Please check network connection.',
        data: null,
      }
    }
  },

  async verifyDeviceOtp(
    emailOrObj: string | { email: string; code: string; deviceId?: string; deviceName?: string },
    codeParam?: string,
    deviceIdParam?: string
  ): Promise<AuthResponse> {
    const email = typeof emailOrObj === 'object' ? emailOrObj.email : emailOrObj
    const code = typeof emailOrObj === 'object' ? emailOrObj.code : (codeParam || '')
    let activeDeviceId =
      typeof emailOrObj === 'object' && emailOrObj.deviceId
        ? emailOrObj.deviceId
        : deviceIdParam || ''

    if (!activeDeviceId && typeof localStorage !== 'undefined') {
      activeDeviceId = localStorage.getItem('p4d_device_id') || ''
    }

    const apiBase =
      typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com')
        ? ''
        : API_BASE

    try {
      const res = await fetch(`${apiBase}/api/v1/auth/verify-device-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          code,
          deviceId: activeDeviceId,
          deviceName: typeof navigator !== 'undefined' ? navigator.userAgent : 'P4D Web Client',
        }),
      })

      const data = await res.json()
      if (data.success || res.ok) {
        const token = data.token || data.data?.token || data.session?.token
        if (token) {
          this.storeToken(token)
        }
        currentUser = data.user || data.data?.user || data.data
        authInitialized = true
        notifyListeners()
        return {
          success: true,
          user: currentUser,
          token,
          message: data.message || 'Device verified.',
          data: { user: currentUser, token, ...data },
          error: null,
        }
      }

      return {
        success: false,
        error: data.error || data.message || 'Invalid verification code.',
        message: data.error || data.message || 'Invalid verification code.',
        data: null,
      }
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || 'OTP verification failed.',
        message: err?.message || 'OTP verification failed.',
        data: null,
      }
    }
  },

  async signUpEmail(
    emailOrObj: string | { name?: string; email: string; password: string; role?: string },
    passwordParam?: string,
    nameParam?: string
  ): Promise<AuthResponse> {
    const email = typeof emailOrObj === 'object' ? emailOrObj.email : emailOrObj
    const password = typeof emailOrObj === 'object' ? emailOrObj.password : (passwordParam || '')
    const name = typeof emailOrObj === 'object' ? emailOrObj.name || '' : (nameParam || '')
    const role = typeof emailOrObj === 'object' && emailOrObj.role ? emailOrObj.role : 'SELLER'

    const apiBase =
      typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com')
        ? ''
        : API_BASE

    try {
      const res = await fetch(`${apiBase}/api/v1/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          name,
          role,
          source: 'unified',
          app: 'parts4drive',
        }),
      })

      const data = await res.json()
      if (data.success || res.ok) {
        const token = data.token || data.data?.token || data.session?.token
        if (token) {
          this.storeToken(token)
        }
        if (!data.requiresVerification && !data.data?.requiresVerification) {
          currentUser = data.data?.user || data.data || data.user
          authInitialized = true
          notifyListeners()
        }
        return {
          success: true,
          requiresVerification: !!(data.requiresVerification || data.data?.requiresVerification),
          requiresDeviceOtp: !!(data.requiresDeviceOtp),
          requiresOtp: !!(data.requiresOtp),
          message: data.message || 'Account registered.',
          data: { user: currentUser, token, ...data },
          error: null,
        }
      }

      const err = new Error(data.error || data.message || 'Registration failed.')
      ;(err as any).status = res.status
      return {
        success: false,
        error: data.error || data.message || 'Registration failed.',
        message: data.error || data.message || 'Registration failed.',
        status: res.status,
        data: null,
      }
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || 'Unable to register account. Please check network connection.',
        message: err?.message || 'Unable to register account. Please check network connection.',
        status: err?.status || 500,
        data: null,
      }
    }
  },

  async signInGoogle(returnTo?: string) {
    const currentOrigin =
      typeof window !== 'undefined' ? window.location.origin : 'https://parts4drive.com'
    const targetReturn =
      returnTo || (typeof window !== 'undefined' ? window.location.href : `${currentOrigin}/login`)
    window.location.href = `${API_BASE}/api/v1/auth/signin-with-google?source=unified&returnTo=${encodeURIComponent(targetReturn)}`
    return { success: true }
  },

  async signOut() {
    const token = this.getToken()
    currentUser = null
    authInitialized = true
    this.clearToken()
    try {
      await fetch(`${API_BASE}/api/v1/sign-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        credentials: 'include',
      })
    } catch (err) {
      console.warn('Sign-out notice:', err)
    }
    notifyListeners()
    if (typeof window !== 'undefined') {
      window.location.href = '/'
    }
  },

  async getSession() {
    try {
      // Auto-extract token from query or hash (e.g. Google OAuth redirect)
      if (typeof window !== 'undefined') {
        const hash = window.location.hash || ''
        const search = window.location.search || ''
        const params = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : search)
        const tokenParam = params.get('token') || params.get('auth_token')
        if (tokenParam) {
          this.storeToken(tokenParam)
          try {
            const url = new URL(window.location.href)
            url.searchParams.delete('token')
            url.searchParams.delete('auth_token')
            const cleanHash = window.location.hash.split('?')[0]
            const newUrl =
              url.pathname +
              (url.search ? url.search : '') +
              (cleanHash && cleanHash !== '#' ? cleanHash : '')
            window.history.replaceState(null, '', newUrl)
          } catch {}
        }
      }

      const token = this.getToken()
      const hasCookie =
        typeof document !== 'undefined' &&
        (document.cookie.includes('better-auth.session_token') ||
          document.cookie.includes('p4d_session_token') ||
          document.cookie.includes('p4d_auth_token') ||
          document.cookie.includes('p4d_marketplace_token'))

      if (!token && !hasCookie) {
        authInitialized = true
        return { data: null, error: null }
      }

      const headers: Record<string, string> = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const res = await fetch(`${API_BASE}/api/v1/auth/me`, {
        headers,
        credentials: 'include',
      })

      if (res.ok) {
        const data = await res.json()
        if (data.success && data.data) {
          currentUser = data.data
          authInitialized = true
          return { data: { user: data.data }, error: null }
        }
      }

      // Central Better-Auth fallback
      try {
        const baRes = await fetch(`${API_BASE}/api/auth/get-session`, {
          headers,
          credentials: 'include',
        })
        if (baRes.ok) {
          const baData = await baRes.json()
          if (baData?.user) {
            currentUser = baData.user
            authInitialized = true
            return { data: { user: baData.user }, error: null }
          }
        }
      } catch {}

      currentUser = null
      authInitialized = true
      this.clearToken()
      notifyListeners()
      return { data: null, error: null }
    } catch {
      currentUser = null
      authInitialized = true
      this.clearToken()
      notifyListeners()
      return { data: null, error: null }
    }
  },

  async requestOtp(email: string, purpose: string = 'email_verification') {
    const res = await fetch(`${API_BASE}/api/v1/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, purpose }),
    })
    return res.json()
  },

  async verifyOtp(email: string, code: string) {
    const apiBase =
      typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com')
        ? ''
        : API_BASE

    const res = await fetch(`${apiBase}/api/v1/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code }),
    })
    const data = await res.json()
    if (data.success) {
      const token = data.token || data.data?.token || data.session?.token
      if (token) {
        this.storeToken(token)
      }
      const rawUser = data.user || data.data?.user || data.data
      currentUser = rawUser ? { ...rawUser, email_verified: 1, emailVerified: true } : rawUser
      authInitialized = true
      notifyListeners()
    }
    return data
  },
}

// Hook to track auth state
export function useSession() {
  const [state, setState] = useState({ data: currentUser, isPending: !authInitialized })

  useEffect(() => {
    if (!authInitialized) {
      authClient.getSession().then(() => {
        setState({ data: currentUser, isPending: false })
      })
    }
  }, [])

  return state
}

export function subscribeToAuth(listener: (sessionOrUser: any) => void) {
  const wrapper = (user: any) => {
    if (!user) {
      listener(null)
      return
    }
    const payload = typeof user === 'object' ? { ...user, user } : user
    listener(payload)
  }
  authListeners.push(wrapper)
  return () => {
    authListeners = authListeners.filter((l) => l !== wrapper)
  }
}

export const getCurrentSession = async () => {
  return authClient.getSession()
}

export const isAuthenticated = async () => {
  const session = await authClient.getSession()
  return !!session.data?.user
}

export const getCurrentUser = async () => {
  const session = await authClient.getSession()
  return session.data?.user || null
}

export const signOut = async () => {
  return authClient.signOut()
}

export default authClient
